/**
 * PointMathUtils - Statistical and geometric utilities for point sets
 *
 * Provides methods for analyzing point distributions, detecting planarity,
 * and plane fitting using RANSAC.
 */

import * as THREE from 'three';

export interface PlanarityResult {
  /** Whether the points form a planar surface */
  isPlanar: boolean;
  /** Distance from origin along the plane normal */
  planeDistance: number;
  /** Standard deviation along the plane normal direction */
  stdDev: number;
  /** Variance along the plane normal direction */
  variance: number;
  /** Center point of the distribution */
  center: THREE.Vector3;
  /** Standard deviation in directions perpendicular to normal */
  perpendicularStdDev: number;
  /** Ratio of perpendicular spread to normal spread */
  spreadRatio: number;
}

export interface PlanarityOptions {
  /** Plane normal direction (default: [0, 0, 1] for horizontal) */
  normal?: THREE.Vector3;
  /** Maximum standard deviation for planarity (default: 0.01 = 1cm) */
  planarThreshold?: number;
  /** Minimum spread in perpendicular directions (default: 0.05 = 5cm) */
  minPerpendicularSpread?: number;
  /** Minimum ratio of perpendicular to normal spread (default: 2.0) */
  minSpreadRatio?: number;
}

export interface PlaneRANSACOptions {
  /** Plane normal direction (if known, RANSAC only fits distance). If not provided, fits full plane. */
  normal?: THREE.Vector3;
  /** Number of RANSAC iterations (default: 200) */
  iterations?: number;
  /** Distance threshold for inliers in meters (default: 0.10) */
  inlierThreshold?: number;
  /** Minimum number of points required (default: 3) */
  minPoints?: number;
}

export interface PlaneResult {
  /** Plane normal (unit vector) */
  normal: THREE.Vector3;
  /** Distance from origin along the normal */
  distance: number;
  /** Points that are inliers to this plane */
  inliers: THREE.Vector3[];
  /** Number of inlier points */
  inlierCount: number;
  /** Confidence score (inlier ratio) */
  confidence: number;
  /** Center of inlier points */
  center: THREE.Vector3;
}

/**
 * Check if a set of 3D points forms a planar surface
 *
 * If no normal is provided, uses PCA to find the best-fit plane normal.
 *
 * @param points - Array of 3D points to analyze
 * @param options - Planarity detection options
 * @returns Planarity analysis result
 */
export function checkPlanarity(points: THREE.Vector3[], options: PlanarityOptions = {}): PlanarityResult {
  const {
    normal,
    planarThreshold = 0.01, // 1cm
    minPerpendicularSpread = 0.05, // 5cm
    minSpreadRatio = 2.0,
  } = options;

  if (points.length === 0) {
    return {
      isPlanar: false,
      planeDistance: 0,
      stdDev: 0,
      variance: 0,
      center: new THREE.Vector3(),
      perpendicularStdDev: 0,
      spreadRatio: 0,
    };
  }

  // Calculate center
  const center = new THREE.Vector3();
  for (const p of points) {
    center.add(p);
  }
  center.divideScalar(points.length);

  // Determine normal: use provided normal or compute via PCA
  let normalizedNormal: THREE.Vector3;
  if (normal) {
    normalizedNormal = normal.clone().normalize();
  } else {
    // Use PCA to find the plane normal (smallest eigenvector of covariance matrix)
    normalizedNormal = computePlaneNormalPCA(points, center);
  }

  // Calculate distance along normal (signed distance from origin)
  let sumDist = 0;
  for (const p of points) {
    sumDist += p.dot(normalizedNormal);
  }
  const avgDist = sumDist / points.length;

  // Calculate variance along normal
  let varianceNormal = 0;
  for (const p of points) {
    const dist = p.dot(normalizedNormal);
    varianceNormal += (dist - avgDist) * (dist - avgDist);
  }
  varianceNormal /= points.length;
  const stdDevNormal = Math.sqrt(varianceNormal);

  // Calculate variance perpendicular to normal (spread in the plane)
  // Project center onto the plane first
  const centerDistToPlane = center.dot(normalizedNormal) - avgDist;
  const projectedCenter = center.clone().sub(normalizedNormal.clone().multiplyScalar(centerDistToPlane));

  let variancePerp = 0;
  for (const p of points) {
    // Project point onto plane
    const distToPlane = p.dot(normalizedNormal) - avgDist;
    const projected = p.clone().sub(normalizedNormal.clone().multiplyScalar(distToPlane));
    // Distance from projected center in plane
    const dx = projected.x - projectedCenter.x;
    const dy = projected.y - projectedCenter.y;
    const dz = projected.z - projectedCenter.z;
    variancePerp += dx * dx + dy * dy + dz * dz;
  }
  variancePerp /= points.length;
  const stdDevPerp = Math.sqrt(variancePerp);

  // Check planarity criteria
  const isFlat = stdDevNormal <= planarThreshold;
  const hasSpread = stdDevPerp >= minPerpendicularSpread;
  const spreadRatio = stdDevPerp / (stdDevNormal + 0.001); // Avoid division by zero
  const hasGoodRatio = spreadRatio >= minSpreadRatio;

  return {
    isPlanar: isFlat && hasSpread && hasGoodRatio,
    planeDistance: avgDist,
    stdDev: stdDevNormal,
    variance: varianceNormal,
    center,
    perpendicularStdDev: stdDevPerp,
    spreadRatio,
  };
}

/**
 * Compute the plane normal using PCA (Principal Component Analysis)
 * Returns the eigenvector corresponding to the smallest eigenvalue,
 * which is the normal to the best-fit plane.
 */
function computePlaneNormalPCA(points: THREE.Vector3[], center: THREE.Vector3): THREE.Vector3 {
  // Build covariance matrix
  let cxx = 0,
    cxy = 0,
    cxz = 0;
  let cyy = 0,
    cyz = 0;
  let czz = 0;

  for (const p of points) {
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    const dz = p.z - center.z;

    cxx += dx * dx;
    cxy += dx * dy;
    cxz += dx * dz;
    cyy += dy * dy;
    cyz += dy * dz;
    czz += dz * dz;
  }

  const n = points.length;
  cxx /= n;
  cxy /= n;
  cxz /= n;
  cyy /= n;
  cyz /= n;
  czz /= n;

  // Use power iteration to find the smallest eigenvector
  // First find largest eigenvector, then deflate, then find next, etc.
  // For simplicity, use analytical solution for 3x3 symmetric matrix

  // Covariance matrix:
  // [cxx cxy cxz]
  // [cxy cyy cyz]
  // [cxz cyz czz]

  // Find eigenvalues using characteristic polynomial
  // For a 3x3 symmetric matrix, we can use the cubic formula
  const eigenResult = computeSmallestEigenvector3x3(cxx, cxy, cxz, cyy, cyz, czz);

  return eigenResult;
}

/**
 * Compute the eigenvector corresponding to the smallest eigenvalue
 * of a 3x3 symmetric matrix using analytical methods.
 */
function computeSmallestEigenvector3x3(a11: number, a12: number, a13: number, a22: number, a23: number, a33: number): THREE.Vector3 {
  // Symmetric matrix:
  // [a11 a12 a13]
  // [a12 a22 a23]
  // [a13 a23 a33]

  // Use Jacobi eigenvalue algorithm for small symmetric matrices
  // This is more numerically stable than the characteristic polynomial

  // Initialize matrix
  const A = [
    [a11, a12, a13],
    [a12, a22, a23],
    [a13, a23, a33],
  ];

  // Initialize eigenvector matrix as identity
  const V = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  // Jacobi rotation iterations
  const maxIter = 50;
  const tolerance = 1e-10;

  for (let iter = 0; iter < maxIter; iter++) {
    // Find largest off-diagonal element
    let maxVal = 0;
    let p = 0,
      q = 1;

    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        if (Math.abs(A[i][j]) > maxVal) {
          maxVal = Math.abs(A[i][j]);
          p = i;
          q = j;
        }
      }
    }

    if (maxVal < tolerance) break;

    // Compute rotation angle
    // Handle the case where A[p][p] == A[q][q] (theta = 0)
    const diff = A[q][q] - A[p][p];
    const denom = 2 * A[p][q];
    let c: number, s: number;

    if (Math.abs(diff) < tolerance) {
      // When diagonal elements are equal, use 45-degree rotation
      c = Math.SQRT1_2;
      s = Math.SQRT1_2;
    } else {
      const theta = diff / denom;
      const sign = theta >= 0 ? 1 : -1;
      const t = sign / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
      c = 1 / Math.sqrt(t * t + 1);
      s = t * c;
    }

    // Apply Jacobi rotation to A
    const App = A[p][p];
    const Aqq = A[q][q];
    const Apq = A[p][q];

    A[p][p] = c * c * App - 2 * s * c * Apq + s * s * Aqq;
    A[q][q] = s * s * App + 2 * s * c * Apq + c * c * Aqq;
    A[p][q] = 0;
    A[q][p] = 0;

    for (let k = 0; k < 3; k++) {
      if (k !== p && k !== q) {
        const Akp = A[k][p];
        const Akq = A[k][q];
        A[k][p] = c * Akp - s * Akq;
        A[p][k] = A[k][p];
        A[k][q] = s * Akp + c * Akq;
        A[q][k] = A[k][q];
      }
    }

    // Apply rotation to eigenvector matrix
    for (let k = 0; k < 3; k++) {
      const Vkp = V[k][p];
      const Vkq = V[k][q];
      V[k][p] = c * Vkp - s * Vkq;
      V[k][q] = s * Vkp + c * Vkq;
    }
  }

  // Eigenvalues are on the diagonal of A
  const eigenvalues = [A[0][0], A[1][1], A[2][2]];

  // Find index of smallest eigenvalue
  let minIdx = 0;
  for (let i = 1; i < 3; i++) {
    if (eigenvalues[i] < eigenvalues[minIdx]) {
      minIdx = i;
    }
  }

  // Return the corresponding eigenvector (column of V)
  const normal = new THREE.Vector3(V[0][minIdx], V[1][minIdx], V[2][minIdx]);
  return normal.normalize();
}

/**
 * Fit a plane to points using RANSAC
 *
 * If normal is provided, only fits the distance (1-parameter fit).
 * If normal is not provided, fits the full plane (4-parameter fit).
 *
 * @param points - Array of 3D points
 * @param options - RANSAC options
 * @returns Best fit plane, or null if no valid plane found
 */
export function fitPlaneRANSAC(points: THREE.Vector3[], options: PlaneRANSACOptions = {}): PlaneResult | null {
  const { normal, iterations = 200, inlierThreshold = 0.1, minPoints = 3 } = options;

  if (points.length < minPoints) {
    return null;
  }

  // If normal is provided, use simplified 1-parameter RANSAC (only fit distance)
  if (normal) {
    return fitPlaneWithKnownNormal(points, normal, iterations, inlierThreshold, minPoints);
  }

  // Full plane fitting (4-parameter RANSAC)
  return fitFullPlaneRANSAC(points, iterations, inlierThreshold, minPoints);
}

/**
 * Fit plane distance using known normal (1-parameter RANSAC)
 */
function fitPlaneWithKnownNormal(points: THREE.Vector3[], normal: THREE.Vector3, iterations: number, inlierThreshold: number, minPoints: number): PlaneResult | null {
  const normalizedNormal = normal.clone().normalize();

  let bestDistance = 0;
  let bestInliers: THREE.Vector3[] = [];
  let bestInlierCount = 0;

  for (let iter = 0; iter < iterations; iter++) {
    // Randomly sample 3 points
    const sample: THREE.Vector3[] = [];
    const indices = new Set<number>();

    while (sample.length < 3 && sample.length < points.length) {
      const idx = Math.floor(Math.random() * points.length);
      if (!indices.has(idx)) {
        indices.add(idx);
        sample.push(points[idx]);
      }
    }

    if (sample.length < 3) continue;

    // Calculate average distance along normal
    let sumDist = 0;
    for (const p of sample) {
      sumDist += p.dot(normalizedNormal);
    }
    const planeDistance = sumDist / sample.length;

    // Count inliers (points within threshold of this plane)
    const inliers: THREE.Vector3[] = [];
    for (const point of points) {
      const distance = Math.abs(point.dot(normalizedNormal) - planeDistance);
      if (distance <= inlierThreshold) {
        inliers.push(point);
      }
    }

    // Keep the best plane (most inliers)
    if (inliers.length > bestInlierCount) {
      bestInlierCount = inliers.length;
      bestInliers = inliers;
      // Refine plane distance using all inliers
      let sumDist = 0;
      for (const inlier of inliers) {
        sumDist += inlier.dot(normalizedNormal);
      }
      bestDistance = sumDist / inliers.length;
    }
  }

  if (bestInlierCount < minPoints) {
    return null;
  }

  // Calculate center of inliers
  const center = new THREE.Vector3();
  for (const p of bestInliers) {
    center.add(p);
  }
  center.divideScalar(bestInliers.length);

  return {
    normal: normalizedNormal,
    distance: bestDistance,
    inliers: bestInliers,
    inlierCount: bestInlierCount,
    confidence: bestInlierCount / points.length,
    center,
  };
}

/**
 * Fit full plane (4-parameter RANSAC: normal + distance)
 */
function fitFullPlaneRANSAC(points: THREE.Vector3[], iterations: number, inlierThreshold: number, minPoints: number): PlaneResult | null {
  let bestNormal = new THREE.Vector3();
  let bestDistance = 0;
  let bestInliers: THREE.Vector3[] = [];
  let bestInlierCount = 0;

  for (let iter = 0; iter < iterations; iter++) {
    // Randomly sample 3 points to define a plane
    const sample: THREE.Vector3[] = [];
    const indices = new Set<number>();

    while (sample.length < 3 && sample.length < points.length) {
      const idx = Math.floor(Math.random() * points.length);
      if (!indices.has(idx)) {
        indices.add(idx);
        sample.push(points[idx]);
      }
    }

    if (sample.length < 3) continue;

    // Calculate plane from 3 points
    const v1 = new THREE.Vector3().subVectors(sample[1], sample[0]);
    const v2 = new THREE.Vector3().subVectors(sample[2], sample[0]);
    const normal = new THREE.Vector3().crossVectors(v1, v2).normalize();

    if (normal.length() < 0.1) continue; // Degenerate plane

    const distance = sample[0].dot(normal);

    // Count inliers
    const inliers: THREE.Vector3[] = [];
    for (const point of points) {
      const dist = Math.abs(point.dot(normal) - distance);
      if (dist <= inlierThreshold) {
        inliers.push(point);
      }
    }

    // Keep the best plane
    if (inliers.length > bestInlierCount) {
      bestInlierCount = inliers.length;
      bestInliers = inliers;
      bestNormal = normal;

      // Refine plane using all inliers
      let sumDist = 0;
      for (const inlier of inliers) {
        sumDist += inlier.dot(normal);
      }
      bestDistance = sumDist / inliers.length;
    }
  }

  if (bestInlierCount < minPoints) {
    return null;
  }

  // Calculate center of inliers
  const center = new THREE.Vector3();
  for (const p of bestInliers) {
    center.add(p);
  }
  center.divideScalar(bestInliers.length);

  return {
    normal: bestNormal,
    distance: bestDistance,
    inliers: bestInliers,
    inlierCount: bestInlierCount,
    confidence: bestInlierCount / points.length,
    center,
  };
}
